import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { TENURE_TYPE_LABELS } from '../../lib/constants';
import type { Parcel } from '../../lib/types';
import { ErrorNote, Field, PageHeader, Spinner, StatusBadge } from '../../ui/components';

function polygonVertexCount(parcel: Parcel): number | null {
  if (parcel.geometry.type !== 'Polygon') return null;
  const rings = parcel.geometry.coordinates as unknown[][];
  return rings[0]?.length ?? null;
}

function ParcelCard({ parcel }: { parcel: Parcel }) {
  const vertices = polygonVertexCount(parcel);
  return (
    <div className="card p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-mono text-xs font-semibold text-slate-400">{parcel.parcelNo}</div>
          <h3 className="text-lg font-bold text-slate-900">{parcel.district}</h3>
        </div>
        <StatusBadge status={parcel.tenureType} />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Area</div>
          <div className="mt-0.5 font-semibold text-slate-900">{parcel.area} ha</div>
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Tenure</div>
          <div className="mt-0.5 font-semibold text-slate-900">{TENURE_TYPE_LABELS[parcel.tenureType] ?? parcel.tenureType}</div>
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Title reference</div>
          <div className="mt-0.5 font-semibold text-slate-900">{parcel.titleRef ?? 'Not yet linked'}</div>
        </div>
      </div>
      <details className="mt-4 text-xs text-slate-400">
        <summary className="cursor-pointer select-none font-semibold">
          Geometry — {parcel.geometry.type}
          {vertices ? ` (${vertices} vertices)` : ''}
        </summary>
        <pre className="mt-2 overflow-x-auto rounded-lg bg-slate-50 p-3 text-slate-600">{JSON.stringify(parcel.geometry, null, 2)}</pre>
      </details>
    </div>
  );
}

export function Parcels() {
  const { persona, loading } = useAuth();
  const [parcelNo, setParcelNo] = useState('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [result, setResult] = useState<Parcel | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const lookupByNo = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(await api.get<Parcel>(`/api/v1/geoserve/parcels/${encodeURIComponent(parcelNo.trim())}`));
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Lookup failed');
    } finally {
      setBusy(false);
    }
  };

  const lookupAt = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(
        await api.get<Parcel>(`/api/v1/geoserve/parcels/at?lat=${encodeURIComponent(lat)}&lng=${encodeURIComponent(lng)}`),
      );
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'No parcel found at that location');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div>
        <PageHeader title="My Parcel" subtitle="Look up a cadastral land parcel by number or by map coordinates." />
        <Spinner label="Checking sign-in…" />
      </div>
    );
  }

  if (!persona) {
    return (
      <div>
        <PageHeader title="My Parcel" subtitle="Look up a cadastral land parcel by number or by map coordinates." />
        <div className="card p-6">
          <p className="text-sm text-slate-600">
            GeoServe, the national land information system behind this lookup, requires every query to be
            attributable to a signed-in account — it keeps a log of who queried what, for audit purposes. That is
            the platform's lowest identity tier (T1), open in principle to any verified citizen, not a special
            government-staff permission.
          </p>
          <p className="mt-2 text-sm text-slate-600">
            Right now this portal's own sign-in flow only issues that kind of session through the MDA staff console
            below — a direct One Citizen sign-in for members of the public who are not government staff is not wired
            up yet (see the build report). Once signed in, any account — staff or otherwise — can use this page;
            no particular role is required beyond being signed in.
          </p>
          <Link to="/staff/login?returnTo=/parcels" className="btn-primary mt-4 inline-flex">
            Sign in to continue
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="My Parcel" subtitle="Look up a cadastral land parcel by number or by map coordinates." />
      <p className="mb-4 text-xs text-slate-500">
        Signed in as {persona.name} · {persona.title}. Browsing every parcel by district requires GIS_ADMIN staff
        access and is not available from this lookup — search by exact parcel number or coordinates instead.
      </p>

      <div className="mb-6 grid gap-4 sm:grid-cols-2">
        <div className="card p-5">
          <h2 className="mb-3 text-sm font-bold text-slate-700">By parcel number</h2>
          <Field label="Parcel number">
            <input
              className="field-input"
              value={parcelNo}
              onChange={(e) => setParcelNo(e.target.value)}
              placeholder="e.g. PS/1102/2019"
            />
          </Field>
          <button className="btn-primary mt-3 w-full" disabled={busy || !parcelNo.trim()} onClick={lookupByNo}>
            {busy ? 'Looking up…' : 'Look up parcel'}
          </button>
        </div>
        <div className="card p-5">
          <h2 className="mb-3 text-sm font-bold text-slate-700">By map coordinates</h2>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Latitude">
              <input className="field-input" value={lat} onChange={(e) => setLat(e.target.value)} placeholder="-4.6231" />
            </Field>
            <Field label="Longitude">
              <input className="field-input" value={lng} onChange={(e) => setLng(e.target.value)} placeholder="55.4531" />
            </Field>
          </div>
          <button className="btn-primary mt-3 w-full" disabled={busy || !lat.trim() || !lng.trim()} onClick={lookupAt}>
            {busy ? 'Looking up…' : 'Find parcel at this point'}
          </button>
        </div>
      </div>

      {error ? (
        <div className="mb-4">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {result ? <ParcelCard parcel={result} /> : null}
    </div>
  );
}
